// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import React, { useContext, useEffect, useId, useRef, useState } from 'react';
import {
    Button,
    ContentLoader,
    Heading,
    Icon,
    Item,
    Label,
    Link,
    Paragraph,
    StatusMessage,
    Text,
    TextArea,
    Toolbar,
    ToolbarExpander,
    useTranslate,
} from '@plesk/ui-library';
import { Locale, Tooltip, UAT } from 'jsw';
import { useLocalStorage } from 'common/hooks';

import { Answer, CopilotContext, createLocalAnswerKey } from '../../CopilotContext';
import AiStylePicker, { AiStyle } from './AiStylePicker';
import ConversationHistory from './ConversationHistory';
import Settings from './Settings';
import MessageItem from 'components/layouts/PageLayout/PageHeader/Copilot/MessageItem';
import useChatScrollRestoration from './useChatScrollRestoration';
import { addToolCallToast } from './ToolCalls/toolCallToast';
import { ToolCallContext } from './ToolCalls/ToolCallContext';
import { composerBlock, latestAssistantRow, stopsAfterRun } from './ToolCalls/toolCalls';
import useToolCallRuns from './ToolCalls/useToolCallRuns';
import useChatStream from './useChatStream';
import { answerFromPayload, userErrorMessage } from './chatStream';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useMutation, useQuery } from '@apollo/client/react';
import COPILOT_CHAT_QUERY from 'queries/CopilotChat.graphql';
import GEN_COPILOT_ANSWER from 'queries/GenCopilotAnswer.graphql';
import GEN_COPILOT_ANSWER_STREAM from 'queries/GenCopilotAnswerStream.graphql';
import SEND_COPILOT_FEEDBACK from 'queries/SendCopilotFeedback.graphql';

enum Role {
    User = 'user',
    Assistant = 'assistant',
}

const SUGGESTIONS = [
    { key: 'welcome.suggest.domain', icon: 'globe' },
    { key: 'welcome.suggest.upload-files', icon: 'arrow-up-tray' },
    { key: 'welcome.suggest.wordpress', icon: 'wordpress' },
    { key: 'welcome.suggest.security', icon: 'shield' },
    { key: 'welcome.suggest.email-phone', icon: 'mail' },
    { key: 'welcome.suggest.spam', icon: 'mail-circle-filled' },
    { key: 'welcome.suggest.slow-website', icon: 'monitoring' },
] as const;

const Translate = Locale.getTranslate('components.copilot');

const Chat = () => {
    const translate = useTranslate('components.copilot');
    const {
        setIsRightSidebarOpen,
        isRightSidebarOpen,
        loading: historyLoading,
        answers, setAnswers,
        chatId, setChatId,
        startNewChat,
        reloadConversation,
    } = useContext(CopilotContext);

    const { data: { config, viewer } = {} } = useQuery(COPILOT_CHAT_QUERY);
    const useStream = config?.copilot?.chat?.useStream ?? true;

    const [genAnswer] = useMutation(GEN_COPILOT_ANSWER);

    const isRightSidebarOpenRef = useRef(isRightSidebarOpen);
    const isHistoryOpenRef = useRef(false);

    const [question, setQuestion] = useState('');
    const [selectedStyle, setSelectedStyle] = useState<AiStyle | null>(null);
    const [isHistoryOpen, setIsHistoryOpen] = useLocalStorage('isCopilotHistoryOpen', false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLFormElement>(null);

    const [loading, setLoading] = useState(false);
    // From sending a question until its answer has arrived in full
    const [asking, setAsking] = useState(false);

    const [feedbackDisabled, setFeedbackDisabled] = useState<Set<Answer['key']>>(new Set());
    // The result is not written to the cache: it would normalize onto the history query's
    // row and re-emit the history
    const [sendFeedback] = useMutation(SEND_COPILOT_FEEDBACK, { fetchPolicy: 'no-cache' });

    const warningId = useId();

    const scrollToBottom = () => {
        const listEl = listRef.current;
        if (listEl) {
            listEl.scrollTop = listEl.scrollHeight;
        }
    };

    const setContentRef = useChatScrollRestoration({ listRef, chatId });

    useEffect(() => {
        if (isRightSidebarOpen && !isRightSidebarOpenRef.current) {
            inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
        }
        isRightSidebarOpenRef.current = isRightSidebarOpen;
    }, [isRightSidebarOpen]);

    useEffect(() => {
        let focusTimeout: ReturnType<typeof setTimeout> | undefined;
        if (!isHistoryOpen && isHistoryOpenRef.current) {
            focusTimeout = setTimeout(() => {
                if (isRightSidebarOpenRef.current && !isHistoryOpenRef.current) {
                    inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
                }
            });
        }
        isHistoryOpenRef.current = isHistoryOpen;
        return () => {
            if (focusTimeout) {
                clearTimeout(focusTimeout);
            }
        };
    }, [isHistoryOpen]);

    useEffect(() => {
        const container = listRef.current;
        if (!container) {
            return;
        }

        const showTooltip = (e: Event) => {
            const el = e.target as HTMLElement;
            if (el.tagName === 'A' && el.hasAttribute('data-title')) {
                const text = el.getAttribute('data-title');
                if (!text) {
                    return;
                }
                Tooltip.init(el, { text });
                Tooltip.set({ element: el, config: { text } });
            }
        };

        container.addEventListener('focus', showTooltip, true);
        container.addEventListener('mouseenter', showTooltip, true);

        return () => {
            container.removeEventListener('focus', showTooltip, true);
            container.removeEventListener('mouseenter', showTooltip, true);
        };
    }, []);

    const scrollSoon = () => {
        setTimeout(() => scrollToBottom());
    };

    /**
     * Refused because this tab's view is stale - typically another tab produced a batch: the
     * question goes back into the composer, and the reloaded history brings in the batch.
     */
    const handleSendRefusal = (message: string, askQuestion: string, questionKey: Answer['key']) => {
        addToolCallToast('warning', message);
        setAnswers(prev => prev.filter(answer => answer.key !== questionKey));
        setQuestion(askQuestion);
        reloadConversation();
    };

    const consumeStream = useChatStream({
        onChunksApplied: focusInput => {
            if (focusInput) {
                setTimeout(() => {
                    scrollToBottom();
                    inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
                });
            } else {
                scrollSoon();
            }
            setLoading(false);
        },
    });

    const processNonStreamResponse = async (askQuestion: string, questionKey: Answer['key']) => {
        const key = createLocalAnswerKey();
        try {
            const result = await genAnswer({ variables: { input: { question: askQuestion, selectedStyle, chatId } } });
            const data = result.data?.genCopilotAnswer;
            if (data?.chatId && data.chatId !== chatId) {
                setChatId(data.chatId);
            }
            if (data) {
                setAnswers(prev => [...prev, answerFromPayload(data, key, chatId)]);
            }
        } catch (e) {
            const refusal = CombinedGraphQLErrors.is(e) ? userErrorMessage(e.errors) : null;
            if (refusal !== null) {
                handleSendRefusal(refusal, askQuestion, questionKey);
            } else {
                setAnswers(prev => [...prev, {
                    key,
                    id: null,
                    role: Role.Assistant,
                    error: String(translate('service-unavailable')),
                }]);
            }
        }
        setTimeout(() => {
            scrollToBottom();
            inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
        });
        setLoading(false);
    };

    const processStreamResponse = async (askQuestion: string, questionKey: Answer['key']) => {
        setLoading(true);
        scrollSoon();

        const outcome = await consumeStream(
            GEN_COPILOT_ANSWER_STREAM,
            { input: { question: askQuestion, stream: true, selectedStyle, chatId } },
            'genCopilotAnswer',
            { focusInput: true },
        );

        if (outcome.refusal !== null) {
            handleSendRefusal(outcome.refusal, askQuestion, questionKey);
        } else if (outcome.received === 0) {
            setAnswers(prev => [...prev, {
                key: createLocalAnswerKey(),
                id: null,
                role: Role.Assistant,
                error: outcome.initialError ?? String(translate('service-unavailable')),
            }]);
            scrollSoon();
        }
        setLoading(false);
    };

    const {
        toolCallActions,
        latestDeciding,
        latestRunInFlight,
        latestRechecking: rechecking,
        latestRunOffered: runOffered,
    } = useToolCallRuns({ useStream, consumeStream, scrollSoon });

    // Only the conversation shown counts: a run or decide request left behind in another one
    // goes on without holding this one up
    const isResponding = loading
        || historyLoading
        || latestRunInFlight
        || latestDeciding
        || Boolean(answers[answers.length - 1]?.streaming);
    // Leaving is safe while tool calls are decided or run, and their continuation is
    // generated: the run goes on server-side, and what it returns is dropped once another
    // conversation is shown. The chunks of a question's answer would land in it instead.
    const navigationBlocked = asking || historyLoading;

    const block = composerBlock(answers);
    // Also while anything is still being answered: a question sent while a run's
    // continuation is being generated would race it on the server
    const sendBlocked = block !== null || isResponding || rechecking;
    const latestCalls = latestAssistantRow(answers)?.toolCalls ?? [];
    // Waiting first: a batch being run is decided and offers no Run. Nothing while a decide
    // request is in flight, which passes by itself, and nothing while a question's own
    // answer loads or streams.
    const composerWarning = (): string | null => {
        if (question.trim() === '') {
            return null;
        }
        if (latestRunInFlight
            && latestCalls.every(call => call.execution !== null)
            // A batch that stops has no continuation: its run is only about to end
            && !stopsAfterRun(latestCalls)
        ) {
            return 'composer.wait.answering';
        }
        if (latestRunInFlight || rechecking) {
            return 'composer.wait.running';
        }
        if (block === 'undecided' && !latestDeciding) {
            return 'composer.blocked.undecided';
        }
        if (block === 'unexecuted' && runOffered) {
            return 'composer.blocked.unexecuted';
        }
        return null;
    };
    const warning = composerWarning();

    const loadAnswer = async (askQuestion = question) => {
        UAT.dispatchAction('AI_ASSISTANT_REQUEST');

        const trimmed = askQuestion.trim();
        // Enter calls this directly, so the composer block is enforced here, not only on the button
        if (!trimmed || loading || isResponding || rechecking || composerBlock(answers) !== null) {
            return;
        }
        // The user row's id is not returned; nothing addresses it, and a history reload fills it in
        const questionKey = createLocalAnswerKey();
        setAnswers(prev => [...prev, { key: questionKey, id: null, role: Role.User, content: trimmed }]);
        scrollSoon();

        setQuestion('');

        setAsking(true);
        try {
            if (!useStream) {
                setLoading(true);
                scrollSoon();
                await processNonStreamResponse(trimmed, questionKey);
            } else {
                await processStreamResponse(trimmed, questionKey);
            }
        } finally {
            setAsking(false);
        }
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        loadAnswer();
    };

    const handleFeedback = async (key: Answer['key'], value: Answer['feedback']) => {
        const targetAnswer = answers.find(a => a.key === key);
        // An unpersisted answer has nothing to attach feedback to
        if (targetAnswer?.id == null || targetAnswer?.chat_id == null) {
            return;
        }

        setFeedbackDisabled(prev => new Set(prev).add(key));
        setTimeout(() => {
            setFeedbackDisabled(prev => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }, 3000);

        const initialFeedbackValue = targetAnswer.feedback;
        const isToggleOff = initialFeedbackValue === value;
        const newValue = isToggleOff ? undefined : value;

        updateAnswerFeedback(key, newValue);

        try {
            await sendFeedback({
                variables: {
                    input: {
                        chatId: targetAnswer.chat_id,
                        id: targetAnswer.id,
                        feedback: newValue,
                    },
                },
            });
        } catch {
            rollbackAnswerFeedback(key, newValue, initialFeedbackValue);
        }
    };

    const updateAnswerFeedback = (key: Answer['key'], value: Answer['feedback']) => {
        setAnswers(prev => prev.map(answer => {
            if (answer.key !== key) {
                return answer;
            }
            return { ...answer, feedback: value };
        }));
    };

    const rollbackAnswerFeedback = (
        key: Answer['key'],
        expectedCurrentValue: Answer['feedback'],
        rollbackValue: Answer['feedback'],
    ) => {
        setAnswers(prev => prev.map(answer => {
            if (answer.key !== key || answer.feedback !== expectedCurrentValue) {
                return answer;
            }
            return { ...answer, feedback: rollbackValue };
        }));
    };

    const allowTools = config?.copilot?.chat?.useTools && viewer?.type === 'ADMIN';

    const chatView = (
        <div className="plesk-copilot-chat-view">
            <div className="plesk-copilot-chat-header">
                <Toolbar>
                    <Heading level={2}>
                        <Icon src={require('images/AI-colored.svg')} />{' '}
                        {translate('title')}
                    </Heading>
                    <ToolbarExpander />
                    <Button icon="plus" ghost tooltip={translate('new-chat')} tooltipAsLabel disabled={navigationBlocked} onClick={startNewChat} />
                    <Button icon="backup2" ghost tooltip={translate('history')} tooltipAsLabel disabled={navigationBlocked} onClick={() => setIsHistoryOpen(true)} />
                    {allowTools && (
                        <Button icon="gear" ghost tooltip={translate('settings')} tooltipAsLabel disabled={navigationBlocked} onClick={() => setIsSettingsOpen(true)} />
                    )}
                    <Button tooltip={translate('close-button-title')} tooltipAsLabel ghost icon="cross-mark" onClick={() => setIsRightSidebarOpen(false)} />
                </Toolbar>
            </div>
            <div className="plesk-copilot-chat-messages" ref={listRef}>
                {historyLoading && <ContentLoader />}
                {!historyLoading && answers.length === 0 ? (
                    <div className="plesk-copilot-welcome">
                        <Item
                            icon={<Icon src={require('images/AI-colored.svg')} />}
                            title={String(translate('welcome.greeting'))}
                            description={<Paragraph>{translate('welcome.description')}</Paragraph>}
                        />
                        <div className="plesk-copilot-suggestions" data-testid="copilot-suggestions">
                            <Text bold>{translate('welcome.try-asking')}</Text>
                            <div className="plesk-copilot-suggestions__labels">
                                {SUGGESTIONS.map(({ key, icon }) => (
                                    <Label
                                        key={key}
                                        icon={icon}
                                        view="outline"
                                        size="lg"
                                        data-action={key}
                                        onClick={() => loadAnswer(String(translate(key)))}
                                    >
                                        {translate(key)}
                                    </Label>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div ref={setContentRef}>
                        <ToolCallContext.Provider value={toolCallActions}>
                            {answers.map(message => (
                                <MessageItem
                                    key={message.key}
                                    message={message}
                                    onFeedback={handleFeedback}
                                    feedbackDisabled={feedbackDisabled.has(message.key)}
                                />
                            ))}
                        </ToolCallContext.Provider>
                        {loading && (
                            <div className="plesk-copilot-msg-row left">
                                <div className="plesk-copilot-assistant-card">
                                    <div className="plesk-copilot-assistant-content">
                                        <ContentLoader
                                            className="plesk-copilot-loading-indicator"
                                            text={<Translate content={'loader.text'} />}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {viewer?.isImpersonated && (
                <StatusMessage intent="warning" className="plesk-copilot-warning" data-testid="copilot-warning-impersonation">
                    <Translate content="impersonation-warning" params={{ name: <b>{viewer.pname}</b> }} />
                </StatusMessage>
            )}
            {warning && (
                <StatusMessage
                    intent="warning"
                    // Spread, as StatusMessageProps lacks id and role, though both reach the element.
                    // The default alert role would interrupt screen readers while typing.
                    {...{ id: warningId, role: undefined }}
                    className="plesk-copilot-warning"
                    data-testid="copilot-composer-warning"
                >
                    {translate(warning)}
                </StatusMessage>
            )}
            <form
                onSubmit={onSubmit}
                className="plesk-copilot-chat-input-row"
                ref={inputRef}
            >
                <TextArea
                    value={question}
                    placeholder={String(translate('chat-input.placeholder'))}
                    disabled={loading}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey && question.trim()) {
                            e.preventDefault();
                            loadAnswer();
                        }
                    }}
                    size="fill"
                    className="plesk-copilot-chat-input"
                    autoheight
                    rows={1}
                />
                {!!question && (
                    <AiStylePicker
                        enabled={!!config?.copilot?.chat?.useStyles}
                        value={selectedStyle}
                        onChange={setSelectedStyle}
                    />
                )}
                {!!question && (
                    <Button
                        ghost
                        icon="arrow-right-circle-filled"
                        type="submit"
                        className={sendBlocked ? 'plesk-copilot-send--blocked' : undefined}
                        disabled={loading || sendBlocked}
                        aria-describedby={warning ? warningId : undefined}
                    />
                )}
            </form>
            <Item
                className="plesk-copilot-privacy"
                icon={<Icon name="shield" size="16" />}
                description={
                    <Translate
                        content="privacy.text"
                        params={{
                            link: <Link href={config?.product.privacyPolicyUrl} target="_blank">{translate('privacy.link')}</Link>,
                        }}
                    />
                }
            />
        </div>
    );

    return (
        <div className="plesk-copilot-chat-container">
            {isHistoryOpen ? (
                <ConversationHistory onBack={() => setIsHistoryOpen(false)} />
            ) : isSettingsOpen ? (
                <Settings onBack={() => setIsSettingsOpen(false)} />
            ) : (
                chatView
            )}
        </div>
    );
};

export default Chat;
